import React, { useState } from 'react';

import { useMutation } from '@apollo/react-hooks';

import deleteIcon from '../../../../../../../../../../assets/img/delete-red.svg';

import styles from '../SliderAnswerRanges.module.css';
import addIcon from '../../../../../../../../../../assets/img/add.svg';
import tickIcon from '../../../../../../../../../../assets/img/tick.svg';
import Loader from '../../../../../../../../Loader/Loader';
import closeIcon from '../../../../../../../../../../assets/img/delete.svg';
import exclamation from '../../../../../../../../../../assets/img/exclamation.svg';

import { UPDATE_QUESTION } from '../../../../../../../../../../graphql/Survey';

export default ({
  setShowSliderAnswerRangesPopup,
  question,
  surveyRefetch
}) => {
  const [ranges, setRanges] = React.useState(
    (question && question.sliderQuestionRanges) || []
  );
  const [isLoading, setIsLoading] = useState(false);

  const [updateQuestion] = useMutation(UPDATE_QUESTION);

  const onAddRangeClick = () => {
    setRanges([...ranges, {}]);
  };

  const onRemoveRangeClick = index => {
    const newRanges = ranges.filter((_, i) => i !== index);
    setRanges(newRanges);
  };

  const checkForTheIssues = () => {
    const { minValue, maxValue } = question;
    const issues = new Set(); // store indexes of ranges with problems

    // Normalize numbers
    const parsedRanges = ranges.map((r, i) => ({
      index: i,
      minValue: Number(r.minValue),
      maxValue: Number(r.maxValue),
      label: r.label
    }));

    // 1. maxValue <= minValue
    parsedRanges.forEach(r => {
      if (r.maxValue < r.minValue) issues.add(r.index);
    });

    // 2. minValue < minValue
    parsedRanges.forEach(r => {
      if (r.minValue < minValue) issues.add(r.index);
    });

    // 3. maxValue > maxValue
    parsedRanges.forEach(r => {
      if (r.maxValue > maxValue) issues.add(r.index);
    });

    // 4. Overlap detection
    for (let i = 0; i < parsedRanges.length; i += 1) {
      for (let j = i + 1; j < parsedRanges.length; j += 1) {
        const a = parsedRanges[i];
        const b = parsedRanges[j];

        const overlap = a.minValue <= b.maxValue && b.minValue <= a.maxValue;

        if (overlap) {
          issues.add(a.index);
          issues.add(b.index);
        }
      }
    }

    // Sort by minValue
    const sorted = [...parsedRanges].sort((a, b) => a.minValue - b.minValue);

    let errorMessage = null;

    // Check gap before the first range
    if (sorted.length > 0 && sorted[0].minValue > minValue) {
      errorMessage = `Missing coverage before minValue value ${
        sorted[0].minValue
      }`;
    }

    // Check internal gaps
    for (let i = 0; i < sorted.length - 1; i += 1) {
      if (sorted[i].maxValue + 1 < sorted[i + 1].minValue) {
        errorMessage = `Missing range between ${sorted[i].maxValue} and ${
          sorted[i + 1].minValue
        }`;
        break;
      }
    }

    // Check gap after last range
    const last = sorted[sorted.length - 1];
    if (!errorMessage && last && last.maxValue < maxValue) {
      errorMessage = `Missing coverage after maxValue value ${last.maxValue}`;
    }

    return {
      errorRangeIndexes: Array.from(issues).sort((a, b) => a - b),
      errorMessage
    };
  };
  const issues = checkForTheIssues();

  const isRangeComplete = range =>
    range.minValue !== undefined &&
    range.maxValue !== undefined &&
    range.label !== undefined &&
    range.minValue !== '' &&
    range.maxValue !== '' &&
    range.label !== '';

  const isValidToComplete = () => {
    const allComplete = ranges.every(isRangeComplete);

    const { errorRangeIndexes, errorMessage } = checkForTheIssues(
      ranges,
      question
    );

    const noErrors = errorRangeIndexes.length === 0 && errorMessage === null;

    return allComplete && noErrors;
  };

  const hasRangeIssue = index =>
    issues &&
    issues.errorRangeIndexes &&
    issues.errorRangeIndexes.indexOf(index) > -1;

  const rangeFieldUpdateChange = (name, value, index) => {
    const newRanges = [...ranges];

    newRanges[index] = {
      ...newRanges[index],
      [name]: name === 'label' ? value : parseInt(value, 10)
    };

    setRanges(newRanges);
  };

  const onConfirmClick = async () => {
    setIsLoading(true);

    const processedRanges = ranges.map(r => {
      const newR = r;

      delete newR.__typename;

      return newR;
    });

    await updateQuestion({
      variables: {
        question: {
          id: question.id,
          sliderQuestionRanges: processedRanges
        }
      }
    });

    await surveyRefetch();

    setShowSliderAnswerRangesPopup(false);
    setIsLoading(false);
  };

  return (
    <div className={styles.popupBackgroundContainer}>
      <div
        className={`${styles.popupContainer} ${styles.popupErrorContainer}`}
        role="presentation"
        onClick={e => e.stopPropagation()}
      >
        <div className={styles.popupContent}>
          <div className={styles.popupTitle}>
            Manage numeric ranges (“buckets”)
          </div>
          <div className={styles.popupBodyContainer}>
            {ranges &&
              ranges.map((r, rI) => (
                <div
                  className={`${styles.rangeContainer} ${
                    hasRangeIssue(rI) ? styles.rangeContainerError : null
                  }`}
                  key={`slider-answer-range-${rI.toString()}`}
                >
                  <input
                    type="number"
                    onKeyDown={evt =>
                      evt.key === 'ArrowDown' ||
                      evt.key === 'ArrowUp' ||
                      evt.key === 'e' ||
                      evt.key === 'E' ||
                      evt.key === '.' ||
                      evt.key === ',' ||
                      evt.key === '-'
                        ? evt.preventDefault()
                        : null
                    }
                    className={styles.rangeInput}
                    placeholder="From"
                    value={r.minValue}
                    onChange={e =>
                      rangeFieldUpdateChange('minValue', e.target.value, rI)
                    }
                  />
                  <input
                    type="number"
                    onKeyDown={evt =>
                      evt.key === 'ArrowDown' ||
                      evt.key === 'ArrowUp' ||
                      evt.key === 'e' ||
                      evt.key === 'E' ||
                      evt.key === '.' ||
                      evt.key === ',' ||
                      evt.key === '-'
                        ? evt.preventDefault()
                        : null
                    }
                    className={styles.rangeInput}
                    placeholder="To"
                    value={r.maxValue}
                    onChange={e =>
                      rangeFieldUpdateChange('maxValue', e.target.value, rI)
                    }
                  />
                  <input
                    className={styles.labelInput}
                    placeholder="Label"
                    value={r.label}
                    onChange={e =>
                      rangeFieldUpdateChange('label', e.target.value, rI)
                    }
                  />
                  <img
                    alt="Delete"
                    src={deleteIcon}
                    className={styles.deleteIcon}
                    role="presentation"
                    onClick={() => onRemoveRangeClick(rI)}
                  />
                </div>
              ))}
            {!ranges || (ranges && ranges.length === 0) ? (
              <div className={styles.noRanges}>No ranges created</div>
            ) : null}
          </div>
          {issues && issues.errorMessage ? (
            <div className={styles.dynamicFilterWarnings}>
              <div className={styles.dynamicFilterWarningMessageBlock}>
                <span className={styles.warningMessage}>
                  <img
                    className={styles.warningIcon}
                    src={exclamation}
                    alt="Warning"
                  />
                  <span>{issues.errorMessage}</span>
                </span>
              </div>
            </div>
          ) : null}
          <div
            className={styles.addRangeButton}
            role="presentation"
            onClick={onAddRangeClick}
          >
            <img className={styles.addIcon} src={addIcon} alt="Add icon" />
            <span>Add numeric range</span>
          </div>
          <div className={styles.footer}>
            <div className={styles.details} />
            <div className={styles.actions}>
              {!isLoading ? (
                <img
                  className={
                    isValidToComplete()
                      ? styles.confirm
                      : `${styles.confirm} ${styles.disabledConfirm}`
                  }
                  src={tickIcon}
                  alt="Confirm icon"
                  onClick={() => {
                    if (isValidToComplete()) {
                      onConfirmClick();
                    }
                  }}
                  role="presentation"
                />
              ) : null}
              {isLoading ? (
                <div
                  style={{
                    display: 'inline-block',
                    bottom: '14px',
                    right: '-9px'
                  }}
                >
                  <Loader size="small" />
                </div>
              ) : null}
              <img
                className={styles.close}
                src={closeIcon}
                alt="Close icon"
                onClick={() => {
                  setShowSliderAnswerRangesPopup(null);
                  setRanges([]);
                }}
                role="presentation"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
